import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Stack, Typography } from '@mui/material';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckOutlined,
  CustomerServiceOutlined,
  ExclamationCircleOutlined,
  FrownOutlined,
  HeartOutlined,
  StopOutlined,
  TagOutlined
} from '@ant-design/icons';
import { Crisp } from 'crisp-sdk-web';
import { useNavigate } from 'react-router-dom';
import useAuth from 'hooks/useAuth';
import { analytics } from 'lib/analytics';
import type { ExitReasonProps } from 'lib/analytics';
import { subscriptionSummary, subscriptionValue } from 'lib/analytics/subscription-value';
import { BILLING_SUBSCRIPTION_STATUSES, normalizeStatus, subscriptionDisplayName } from 'lib/billing';
import type { LibSubscription } from 'lib/billing/pbAdapter';
import { useBillingStore } from 'sections/account-settings/subscriptions/checkout-v2/store';
import { formatEndsOn } from 'sections/account-settings/subscriptions/checkout-v2/Configure/formatEndsOn';
import { openSupportChat } from 'utils/supportChat';
import { knownFreezeMoney } from 'features/subscription-freeze/formatters';
import { FREEZE_STATUS_PATH } from 'features/subscription-freeze/paths';
import { useOpenTradeAccounts } from 'features/subscription-freeze/useOpenTradeAccounts';
import { usePauseCreate } from 'features/subscription-freeze/usePauseCreate';
import { usePauseOffer } from 'features/subscription-freeze/usePauseOffer';
import { cancellationConsequences, cancellationScope, greetingName, planGroups, sharedPeriodEnd } from '../billing';
import {
  CONFIRM_STEP,
  CRISP_CANCELLED_COLOR,
  CRISP_CANCELLED_EVENT,
  CancellationStep,
  DONE_STEP,
  BACK,
  KEEP_PLAN,
  OFFER_STEP,
  OFFER_TAKEN_STEP,
  PAUSE_REVIEW_STEP,
  PAUSE_STEP,
  PAUSE_UNAVAILABLE_STEP,
  PROGRESS_POSITION,
  PROGRESS_TOTAL,
  REASON_STEP,
  SUPPORT_STEP
} from '../content';
import { InterventionAction, INTERVENTIONS } from '../interventions';
import { LeaveRequest, useLeaveDialog } from '../leaveDialogStore';
import { cancelOfferView, offerChargeDate, offerPercent, ReadyCancelOffer } from '../offers';
import { ExitAnswer, ExitReason, exitComment } from '../reasons';
import { useCancelOffer } from '../useCancelOffer';
import { useCancelSubscriptions } from '../useCancelSubscriptions';
import { useCancellationSurvey } from '../useCancellationSurvey';
import CancellationConfirmStep from './CancellationConfirmStep';
import CancellationReasonStep from './CancellationReasonStep';
import CancellationSupportStep from './CancellationSupportStep';
import DialogStep, { DialogLoading, StepAction } from './DialogStep';
import PastDueNotice from './PastDueNotice';
import OfferCard from './OfferCard';
import PauseOfferHero from './PauseOfferHero';
import PauseReviewStep from './PauseReviewStep';
import PauseUnavailable from './PauseUnavailable';
import StepDialog from './StepDialog';
import { Callout, Group, GroupList, PlanGroupRow, SNOWFLAKE_ICON } from './StepParts';

const TITLE_ID = 'cancellation-dialog-title';

// Button icons are decoration: hidden, so each button is named by its label alone.
const KEEP_ICON = <HeartOutlined aria-hidden />;
const NEXT_ICON = <ArrowRightOutlined aria-hidden />;
const BACK_ICON = <ArrowLeftOutlined aria-hidden />;

// Every way on toward the cancel looks the same: toned, pointing ahead, quieter than what it declines.
const towardCancel = (label: string, onClick: () => void): StepAction => ({ label, onClick, tone: 'neutral', endIcon: NEXT_ICON });

type CancelTrack = typeof analytics.cancelIntended;

// The same per-subscription events checkout-v2 fires, so one funnel counts every way out.
const trackEach = (subscriptions: LibSubscription[], track: CancelTrack) =>
  subscriptions.forEach((subscription) => track({ ...subscriptionValue(subscription), is_partial: false }));

const offerProps = (offer: ReadyCancelOffer) => ({
  percent_bps: offer.percentBps,
  interval: offer.nextCharge.interval,
  interval_count: offer.nextCharge.intervalCount
});

/** A pause first when one is open to them, then reason, help and confirm: cancels the request's subscriptions at period end. */
function CancellationDialog({ request, onClose }: { request: LeaveRequest; onClose: () => void }) {
  const { user } = useAuth() as any;
  const subscriptions = useBillingStore((state) => state.subscriptions) as LibSubscription[];
  const catalog = useBillingStore((state) => state.catalog);
  const { pending, busy: cancelling, error, cancelAll } = useCancelSubscriptions(request.subscriptions);
  const scope = useMemo(() => cancellationScope(pending, catalog), [pending, catalog]);
  const consequences = useMemo(() => cancellationConsequences(scope), [scope]);
  const hasPastDue = subscriptions.some((sub) => normalizeStatus(sub) === BILLING_SUBSCRIPTION_STATUSES.past_due);

  const { offer: pauseOffer, reload: reloadPauseOffer } = usePauseOffer(request.subscriptions);
  const pauseQuote = pauseOffer.status === 'ready' ? pauseOffer.quote : undefined;
  const pauseDays = pauseQuote?.durationDays || 0;
  const restartsOn = (pauseQuote && formatEndsOn(pauseQuote.plannedResumeAt)) || '';
  const restartCharge = pauseQuote && knownFreezeMoney(pauseQuote.restartAmount, pauseQuote.restartCurrency);
  const offerWaiting = Boolean(pauseQuote?.cancelOfferWaiting);
  const { pause, busy: pausing } = usePauseCreate();
  const navigate = useNavigate();
  const [step, setStep] = useState<CancellationStep>(pauseOffer.status === 'none' ? 'reason' : 'pause');
  const openTrades = useOpenTradeAccounts(step === 'pauseReview');
  const [pauseAcknowledged, setPauseAcknowledged] = useState(false);
  const [pauseError, setPauseError] = useState('');
  const [reason, setReason] = useState<ExitReason>();
  const [comment, setComment] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const intentSent = useRef(false);

  // Asked for as soon as they pick price as the reason, so the offer is usually there when they continue.
  const priceReason = reason === 'too_expensive';
  const { state: offerState, reload: reloadOffer, accept: acceptOffer, busy: accepting } = useCancelOffer(priceReason);
  const offer = offerState.status === 'ready' ? offerState.offer : undefined;
  // No new offer for someone whose last one still waits: the help card reminds them what cancelling gives up.
  const waiting = offerState.status === 'none' ? offerState.waiting : undefined;
  const [offerError, setOfferError] = useState('');
  const offerShown = useRef(false);
  // The renewal bills every line of its subscription, listed the way the confirm step lists plans.
  const renewalPlans = useCallback(
    (stripeId: string) =>
      planGroups(
        subscriptions.filter((candidate) => candidate.stripeId === stripeId),
        catalog
      ),
    [subscriptions, catalog]
  );
  const offerView = useMemo(() => offer && cancelOfferView(offer, renewalPlans), [offer, renewalPlans]);
  // Price as the reason is answered by the offer when there is one, and by the help card when not.
  const helpStep: CancellationStep = priceReason && offerState.status !== 'none' ? 'offer' : 'support';
  const busy = cancelling || pausing || accepting;

  const survey = useCancellationSurvey(step === 'reason');
  const answer: ExitAnswer | undefined = reason && { reason, comment: exitComment({ reason, comment }) };
  // The answer they finally go with rides on the leave events, so reasons can be read against outcomes.
  const exit: ExitReasonProps = answer
    ? { exit_reason: answer.reason, ...(survey.submissionId && { exit_submission_id: survey.submissionId }) }
    : {};

  // No pause to offer, or none any more: the visit goes on at the reason, as if the offer was never there.
  useEffect(() => {
    if (pauseOffer.status === 'none') setStep((current) => (current === 'pause' || current === 'pauseReview' ? 'reason' : current));
    // Refused since the review opened (their billing moved): the first screen now says why.
    if (pauseOffer.status === 'unavailable') setStep((current) => (current === 'pauseReview' ? 'pause' : current));
  }, [pauseOffer.status]);

  // No offer for them after all: price gets the help card instead.
  useEffect(() => {
    if (offerState.status === 'none') setStep((current) => (current === 'offer' ? 'support' : current));
  }, [offerState.status]);

  useEffect(() => {
    if (step !== 'offer' || !offer || offerShown.current) return;
    offerShown.current = true;
    analytics.leaveOfferShown({ ...subscriptionSummary(pending), ...offerProps(offer) });
  }, [step, offer, pending]);

  // Opening is the intent; leaving without confirming is the abort.
  useEffect(() => {
    if (intentSent.current) return;
    intentSent.current = true;
    trackEach(request.subscriptions, analytics.cancelIntended);
    analytics.leaveStarted(subscriptionSummary(request.subscriptions));
  }, [request.subscriptions]);

  const keep = () => {
    if (busy) return;
    trackEach(pending, analytics.cancelAborted);
    analytics.leaveAbandoned({
      ...subscriptionSummary(pending),
      step,
      ...exit,
      ...(answer?.comment && { exit_comment: answer.comment })
    });
    survey.dismiss();
    onClose();
  };
  // Once cancelled, or kept with the offer, there is nothing left to abandon.
  const close = step === 'done' || step === 'offerTaken' ? onClose : keep;
  const backTo = (previous: CancellationStep): StepAction => ({
    label: BACK,
    onClick: () => setStep(previous),
    tone: 'ghost',
    startIcon: BACK_ICON
  });

  // The chat cannot take focus from inside a modal, so choosing support ends the visit first.
  const talkToSupport = () => {
    if (busy) return;
    keep();
    openSupportChat();
  };

  const confirmPause = async () => {
    if (!pauseQuote) return;
    setPauseError('');
    const result = await pause(pauseQuote);
    if (result.outcome === 'billingChanged') {
      // New figures need a fresh read and a fresh tick.
      setPauseError(PAUSE_REVIEW_STEP.billingChanged);
      setPauseAcknowledged(false);
      reloadPauseOffer();
      return;
    }
    if (result.outcome === 'failed') {
      setPauseError(result.message);
      return;
    }
    const summary = subscriptionSummary(pending);
    trackEach(pending, analytics.cancelAborted);
    analytics.subscriptionFrozen({ ...summary, duration_days: pauseDays });
    analytics.leavePaused({ ...summary, duration_days: pauseDays });
    onClose();
    navigate(FREEZE_STATUS_PATH);
  };

  const takeOffer = async () => {
    if (!offer) return;
    setOfferError('');
    const result = await acceptOffer();
    if (result.outcome === 'billingChanged') {
      setOfferError(OFFER_STEP.billingChanged);
      reloadOffer();
      return;
    }
    if (result.outcome !== 'accepted') {
      setOfferError(result.outcome === 'alreadyTaken' ? OFFER_STEP.alreadyTaken : result.message);
      return;
    }
    // They stay: nothing was cancelled, and the survey only records people who left.
    trackEach(pending, analytics.cancelAborted);
    analytics.leaveOfferAccepted({ ...subscriptionSummary(pending), ...offerProps(offer), ...exit });
    survey.dismiss();
    setStep('offerTaken');
  };

  const runIntervention = (action: InterventionAction) => {
    if (action.kind === 'support') {
      talkToSupport();
      return;
    }
    // Opened alongside, so the dialog stays where it is.
    window.open(action.to, '_blank', 'noopener,noreferrer');
  };

  const confirm = async () => {
    const cancelled = await cancelAll();
    if (!cancelled) return;
    analytics.leaveCompleted({ ...subscriptionSummary(cancelled), ...exit });
    // The survey answer goes with the cancellation, so its responses are only people who left.
    if (answer) survey.send(answer);
    try {
      Crisp.session.pushEvent(CRISP_CANCELLED_EVENT, undefined, CRISP_CANCELLED_COLOR as any);
    } catch {
      /* support tagging never blocks the cancel */
    }
    request.onCancelled?.();
    setStep('done');
  };

  const progressIndex = PROGRESS_POSITION[step];
  const frame = {
    titleId: TITLE_ID,
    progress: progressIndex === undefined ? undefined : { index: progressIndex, total: PROGRESS_TOTAL },
    onClose: close,
    busy
  };
  // The closing steps (offer taken, cancelled) share one celebratory frame.
  const successStep = (title: string, description: string, doneLabel: string) => (
    <DialogStep
      {...frame}
      icon={<CheckOutlined />}
      tone="success"
      celebrate
      title={title}
      description={description}
      primary={{ label: doneLabel, onClick: onClose, tone: 'ink', startIcon: <CheckOutlined aria-hidden /> }}
    />
  );

  let body;
  if (step === 'pause' && pauseOffer.status === 'unavailable') {
    body = (
      <DialogStep
        {...frame}
        icon={SNOWFLAKE_ICON}
        tone="ice"
        title={PAUSE_UNAVAILABLE_STEP.title}
        description={PAUSE_UNAVAILABLE_STEP.description}
        primary={towardCancel(PAUSE_STEP.continue, () => setStep('reason'))}
        secondary={{ label: KEEP_PLAN, onClick: keep, tone: 'soft', startIcon: KEEP_ICON }}
      >
        <PauseUnavailable quote={pauseOffer.quote} onSupport={talkToSupport} />
      </DialogStep>
    );
  } else if (step === 'pause') {
    body =
      pauseOffer.status === 'ready' ? (
        <DialogStep
          {...frame}
          icon={SNOWFLAKE_ICON}
          tone="ice"
          title={PAUSE_STEP.title}
          description={PAUSE_STEP.description(pauseDays, request.subscriptions.length)}
          primary={{ label: PAUSE_STEP.review, onClick: () => setStep('pauseReview'), tone: 'ice', endIcon: NEXT_ICON }}
          secondary={towardCancel(PAUSE_STEP.continue, () => setStep('reason'))}
        >
          <PauseOfferHero days={pauseDays} restartsOn={restartsOn} />
        </DialogStep>
      ) : (
        <DialogLoading titleId={TITLE_ID} title={PAUSE_STEP.loading} onClose={close} />
      );
  } else if (step === 'pauseReview') {
    // Open trades only warn, never block; Pause waits for the check so the warning is seen first.
    const pauseHint = pauseQuote && !openTrades.loading && !pausing && !pauseAcknowledged ? PAUSE_REVIEW_STEP.acknowledgeFirst : '';
    body = (
      <DialogStep
        {...frame}
        icon={SNOWFLAKE_ICON}
        tone="ice"
        title={PAUSE_REVIEW_STEP.title}
        description={PAUSE_REVIEW_STEP.description}
        primary={{
          label: pausing ? PAUSE_REVIEW_STEP.busy : PAUSE_STEP.pause(pauseDays),
          onClick: confirmPause,
          tone: 'ice',
          startIcon: SNOWFLAKE_ICON,
          loading: pausing,
          disabled: !pauseQuote || !pauseAcknowledged || openTrades.loading
        }}
        primaryHint={pauseHint}
        secondary={backTo('pause')}
      >
        <PauseReviewStep
          restartsOn={restartsOn}
          restartCharge={restartCharge}
          offerWaiting={offerWaiting}
          consequences={consequences}
          dedicatedEnvs={scope.dedicatedEnvs}
          rooms={scope.rooms}
          openTrades={openTrades}
          acknowledged={pauseAcknowledged}
          onAcknowledgedChange={setPauseAcknowledged}
          error={pauseError}
          disabled={pausing}
        />
      </DialogStep>
    );
  } else if (step === 'reason') {
    const next: StepAction = {
      label: REASON_STEP.continue,
      onClick: () => setStep(helpStep),
      tone: 'ink',
      endIcon: NEXT_ICON,
      disabled: !answer
    };
    body = (
      <DialogStep
        {...frame}
        icon={<FrownOutlined />}
        tone="primary"
        title={REASON_STEP.title(greetingName(user?.firstName))}
        description={REASON_STEP.description}
        primary={next}
        secondary={{ label: KEEP_PLAN, onClick: keep, tone: 'soft', startIcon: KEEP_ICON }}
      >
        <CancellationReasonStep value={reason} onChange={setReason} comment={comment} onCommentChange={setComment} />
      </DialogStep>
    );
  } else if (step === 'support' && answer) {
    const intervention = INTERVENTIONS[answer.reason];
    const next = towardCancel(SUPPORT_STEP.continue, () => setStep('confirm'));
    body = (
      <DialogStep
        {...frame}
        icon={<CustomerServiceOutlined />}
        tone="primary"
        title={intervention.title}
        description={intervention.description}
        primary={next}
        secondary={backTo('reason')}
      >
        <Stack spacing={2}>
          {waiting && (
            <Callout icon={<TagOutlined />} tone="primary" title={SUPPORT_STEP.waitingTitle(offerPercent(waiting.percentBps))}>
              <Typography variant="body2" color="text.secondary">
                {SUPPORT_STEP.waitingBody(offerChargeDate(waiting.nextCharge))}
              </Typography>
            </Callout>
          )}
          <CancellationSupportStep answer={answer} onAction={runIntervention} />
        </Stack>
      </DialogStep>
    );
  } else if (step === 'offer') {
    body = offerView ? (
      <DialogStep
        {...frame}
        icon={<TagOutlined />}
        tone="primary"
        title={offerView.title}
        description={offerView.description}
        primary={{
          label: accepting ? OFFER_STEP.busy : OFFER_STEP.use,
          onClick: takeOffer,
          tone: 'ink',
          startIcon: <TagOutlined aria-hidden />,
          loading: accepting
        }}
        secondary={towardCancel(OFFER_STEP.cancelAnyway, () => setStep('confirm'))}
      >
        <Stack spacing={2}>
          {offerError && <Alert severity="error">{offerError}</Alert>}
          <OfferCard {...offerView.card}>
            {offerView.renewal.plans.length > 0 && (
              <GroupList>
                <Group label={offerView.renewal.label}>
                  {offerView.renewal.plans.map((plan) => (
                    <PlanGroupRow key={plan.key} group={plan} />
                  ))}
                </Group>
              </GroupList>
            )}
          </OfferCard>
        </Stack>
      </DialogStep>
    ) : (
      <DialogLoading titleId={TITLE_ID} title={OFFER_STEP.loading} onClose={close} />
    );
  } else if (step === 'offerTaken') {
    body = successStep(OFFER_TAKEN_STEP.title, OFFER_TAKEN_STEP.description(offerPercent(offer?.percentBps ?? 0)), OFFER_TAKEN_STEP.done);
  } else if (step === 'done') {
    const endsOn = sharedPeriodEnd(request.subscriptions);
    body = successStep(DONE_STEP.title, DONE_STEP.description(endsOn ? formatEndsOn({ seconds: endsOn }) : null), DONE_STEP.done);
  } else {
    const cancel: StepAction = {
      label: cancelling ? CONFIRM_STEP.busy : CONFIRM_STEP.cancel,
      onClick: confirm,
      tone: 'dangerSoft',
      startIcon: <StopOutlined aria-hidden />,
      loading: cancelling,
      disabled: !acknowledged
    };
    // One plan is named, as the design does; several are listed below the question.
    const only = pending.length === 1 ? subscriptionDisplayName(pending[0]) : undefined;
    body = (
      <DialogStep
        {...frame}
        icon={<ExclamationCircleOutlined />}
        tone="error"
        title={CONFIRM_STEP.title(only)}
        description={CONFIRM_STEP.description(pending.length)}
        primary={{ label: KEEP_PLAN, onClick: keep, tone: 'ink', startIcon: KEEP_ICON }}
        secondary={cancel}
      >
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          {hasPastDue && <PastDueNotice onSupport={talkToSupport} />}
          <CancellationConfirmStep
            subscriptions={pending}
            catalog={catalog}
            consequences={consequences}
            acknowledged={acknowledged}
            onAcknowledgedChange={setAcknowledged}
            disabled={cancelling}
          />
        </Stack>
      </DialogStep>
    );
  }

  return (
    <StepDialog titleId={TITLE_ID} onClose={close}>
      {/* Keyed by step, so each one fades in fresh. */}
      <Fragment key={step}>{body}</Fragment>
    </StepDialog>
  );
}

/** Mounted once in the app: shows the dialog whenever a screen asks for it. */
export default function CancellationDialogHost() {
  const request = useLeaveDialog((state) => state.request);
  const close = useLeaveDialog((state) => state.close);
  if (!request) return null;
  return <CancellationDialog key={request.key} request={request} onClose={close} />;
}
